import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useCallback } from 'react'
import {
    getOnboardingState,
    completeOnboardingStep,
    resetOnboardingState,
    dismissOnboarding,
} from './onboardingApi'

/**
 * Onboarding step keys
 */
export const ONBOARDING_STEPS = {
    ASK_QUESTION: 'askQuestion',
    OPEN_CHART: 'openChart',
    SAVE_TO_CANVAS: 'saveToCanvas',
}

/**
 * Step definitions with labels and order
 */
export const STEP_DEFINITIONS = [
    {
        key: ONBOARDING_STEPS.ASK_QUESTION,
        label: 'Ask Scoop a Question',
        description: 'Type a question and press Enter or click Send',
        order: 1,
    },
    {
        key: ONBOARDING_STEPS.OPEN_CHART,
        label: 'Open Chart in Explorer',
        description: 'Ask Scoop for a chart, then click "Open in Explorer" to analyze it',
        order: 2,
    },
    {
        key: ONBOARDING_STEPS.SAVE_TO_CANVAS,
        label: 'Save to Canvas',
        description: 'Click "Save Summary" and select a canvas',
        order: 3,
    },
]

/**
 * Features and which steps unlock them
 * Note: Explorer is unlocked after step 2 (OPEN_CHART) so users must use
 * the "Open in Explorer" button in charts first (which bypasses this gating)
 */
const FEATURE_UNLOCK_MAP = {
    chat: [], // Always unlocked
    explorer: [ONBOARDING_STEPS.ASK_QUESTION, ONBOARDING_STEPS.OPEN_CHART], // Unlocked after step 2
    canvas: [
        ONBOARDING_STEPS.ASK_QUESTION,
        ONBOARDING_STEPS.OPEN_CHART,
        ONBOARDING_STEPS.SAVE_TO_CANVAS,
    ], // Unlocked after all steps
    sources: [
        ONBOARDING_STEPS.ASK_QUESTION,
        ONBOARDING_STEPS.OPEN_CHART,
        ONBOARDING_STEPS.SAVE_TO_CANVAS,
    ], // Unlocked after all steps
    liveWorksheets: [
        ONBOARDING_STEPS.ASK_QUESTION,
        ONBOARDING_STEPS.OPEN_CHART,
        ONBOARDING_STEPS.SAVE_TO_CANVAS,
    ], // Unlocked after all steps
}

/**
 * Hook for managing onboarding state and progress
 */
export const useOnboarding = () => {
    const queryClient = useQueryClient()
    const userID = useSelector((state) => state.auth?.userID)

    // ==========================================================================
    // Queries
    // ==========================================================================

    const {
        data: onboarding,
        isLoading: loadingOnboarding,
        error: onboardingError,
        refetch: refetchOnboarding,
    } = useQuery({
        queryKey: ['onboarding', userID],
        queryFn: () => getOnboardingState(userID),
        enabled: Boolean(userID),
        staleTime: 1000 * 60 * 5, // 5 minutes
    })

    // ==========================================================================
    // Mutations
    // ==========================================================================

    const { mutate: completeStep, isPending: isCompletingStep } = useMutation({
        mutationFn: ({ stepKey, metadata }) => completeOnboardingStep(userID, stepKey, metadata),
        onSuccess: (newState, variables) => {
            // Update cache immediately for optimistic UI
            queryClient.setQueryData(['onboarding', userID], newState)
            // Invalidate to ensure all components re-render with fresh data
            queryClient.invalidateQueries({ queryKey: ['onboarding', userID] })
            if (variables?.onSuccess) variables.onSuccess(newState)
        },
        onError: (error, variables) => {
            if (variables?.onError) variables.onError(error)
        },
    })

    const { mutate: reset, isPending: isResetting } = useMutation({
        mutationFn: () => resetOnboardingState(userID),
        onSuccess: (newState, variables) => {
            queryClient.setQueryData(['onboarding', userID], newState)
            queryClient.invalidateQueries({ queryKey: ['onboarding', userID] })
            if (variables?.onSuccess) variables.onSuccess(newState)
        },
    })

    const { mutate: dismiss, isPending: isDismissing } = useMutation({
        mutationFn: () => dismissOnboarding(userID),
        onSuccess: (newState, variables) => {
            queryClient.setQueryData(['onboarding', userID], newState)
            queryClient.invalidateQueries({ queryKey: ['onboarding', userID] })
            if (variables?.onSuccess) variables.onSuccess(newState)
        },
    })

    // ==========================================================================
    // Helper Functions (memoized to prevent infinite re-renders)
    // ==========================================================================

    /**
     * Check if a specific step is completed
     */
    const isStepCompleted = useCallback(
        (stepKey) => {
            return Boolean(onboarding?.steps?.[stepKey]?.completed)
        },
        [onboarding?.steps]
    )

    /**
     * Check if onboarding is complete (all steps done)
     */
    const isOnboardingComplete = useCallback(() => {
        if (!onboarding?.steps) return false
        return (
            onboarding.steps.askQuestion?.completed === true &&
            onboarding.steps.openChart?.completed === true &&
            onboarding.steps.saveToCanvas?.completed === true
        )
    }, [onboarding?.steps])

    /**
     * Check if onboarding checklist is dismissed
     */
    const isDismissed = useCallback(() => {
        return Boolean(onboarding?.dismissed)
    }, [onboarding?.dismissed])

    /**
     * Check if a feature is unlocked based on completed steps
     * @param {string} feature - 'chat' | 'explorer' | 'canvas'
     */
    const isFeatureUnlocked = useCallback(
        (feature) => {
            if (!onboarding?.steps) return feature === 'chat' // Only chat unlocked by default
            const requiredSteps = FEATURE_UNLOCK_MAP[feature] || []
            return requiredSteps.every((stepKey) => onboarding.steps[stepKey]?.completed === true)
        },
        [onboarding?.steps]
    )

    /**
     * Get the next incomplete step
     */
    const getNextStep = useCallback(() => {
        if (!onboarding?.steps) return STEP_DEFINITIONS[0]

        for (const step of STEP_DEFINITIONS) {
            if (!onboarding.steps[step.key]?.completed) {
                return step
            }
        }
        return null // All complete
    }, [onboarding?.steps])

    /**
     * Get completed step count
     */
    const getCompletedCount = useCallback(() => {
        if (!onboarding?.steps) return 0
        return STEP_DEFINITIONS.filter((step) => onboarding.steps[step.key]?.completed === true)
            .length
    }, [onboarding?.steps])

    /**
     * Get progress percentage
     */
    const getProgressPercentage = useCallback(() => {
        if (!onboarding?.steps) return 0
        const completedCount = STEP_DEFINITIONS.filter(
            (step) => onboarding.steps[step.key]?.completed === true
        ).length
        return Math.round((completedCount / STEP_DEFINITIONS.length) * 100)
    }, [onboarding?.steps])

    /**
     * Get steps with completion status
     */
    const getStepsWithStatus = useCallback(() => {
        return STEP_DEFINITIONS.map((step) => ({
            ...step,
            completed: Boolean(onboarding?.steps?.[step.key]?.completed),
            metadata: onboarding?.steps?.[step.key] || {},
        }))
    }, [onboarding?.steps])

    // ==========================================================================
    // Return
    // ==========================================================================

    return {
        // State
        onboarding,
        loadingOnboarding,
        onboardingError,
        refetchOnboarding,

        // Mutations
        completeStep,
        isCompletingStep,
        reset,
        isResetting,
        dismiss,
        isDismissing,

        // Helpers
        isStepCompleted,
        isOnboardingComplete,
        isDismissed,
        isFeatureUnlocked,
        getNextStep,
        getCompletedCount,
        getProgressPercentage,
        getStepsWithStatus,

        // Constants
        STEPS: ONBOARDING_STEPS,
        STEP_DEFINITIONS,
    }
}
